import { createContext, useContext, useMemo } from 'react';
import type { Money } from '@urbanrats/contracts';
import { approxConvert, type ApproxResult } from '@urbanrats/domain';
import type { ClientGateways } from '../gateway/index.ts';

const KEY = 'urbanrats.display.v1';

export interface DisplayPrefs {
  /** Approximate display currency; null = off. Never the settlement currency. */
  displayCurrency: string | null;
  /** Region the player picked for suggestions (language / display currency). Not an entitlement. */
  region: string | null;
}

export function loadDisplayPrefs(): DisplayPrefs {
  try {
    const p = JSON.parse(localStorage.getItem(KEY) ?? '{}') as Partial<DisplayPrefs>;
    return { displayCurrency: typeof p.displayCurrency === 'string' ? p.displayCurrency : null, region: typeof p.region === 'string' ? p.region : null };
  } catch {
    return { displayCurrency: null, region: null };
  }
}

export function saveDisplayPrefs(p: DisplayPrefs): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(p));
  } catch {
    /* convenience only */
  }
}

/** Region → suggested language and display currency. A suggestion only: the wallet never changes. */
export const REGION_SUGGESTIONS: Record<string, { locale: 'en' | 'tr'; display: string | null }> = {
  TR: { locale: 'tr', display: 'TRY' },
  DE: { locale: 'en', display: 'EUR' },
  US: { locale: 'en', display: 'USD' },
  GB: { locale: 'en', display: null },
};

/** Current approximate value of an amount in the display currency (re-evaluated as quotes age). */
export function useApprox(gw: ClientGateways, displayCurrency: string | null, nowMs: number): (m: Money) => ApproxResult | null {
  // Re-read the quote list every 5 s of (server) time; expiry itself is judged against nowMs.
  const bucket = Math.floor(nowMs / 5000);
  const quotes = useMemo(() => gw.fx.quotes(), [gw, bucket]); // eslint-disable-line react-hooks/exhaustive-deps -- bucket is the refresh trigger
  return (m: Money) => {
    if (!displayCurrency || displayCurrency === m.currency) return null;
    const target = gw.fx.displayCurrencies.find((d) => d.code === displayCurrency);
    if (!target) return null;
    return approxConvert(m, { currency: target.code, scale: target.scale }, quotes, nowMs);
  };
}

export interface DisplayCtx {
  prefs: DisplayPrefs;
  setPrefs: (p: DisplayPrefs) => void;
  approx: (m: Money) => ApproxResult | null;
}

export const DisplayContext = createContext<DisplayCtx>({ prefs: { displayCurrency: null, region: null }, setPrefs: () => undefined, approx: () => null });
export const useDisplay = () => useContext(DisplayContext);
